import { useEffect, useState } from "react";

export type TableColumnWidth = "compact" | "standard" | "wide";
export type TableRowHeight = "compact" | "standard" | "comfortable";

export interface TableDisplayPreferences {
  columnWidth: TableColumnWidth;
  rowHeight: TableRowHeight;
}

export const DEFAULT_TABLE_DISPLAY: Readonly<TableDisplayPreferences> = Object.freeze({
  columnWidth: "standard",
  rowHeight: "standard",
});

const STORAGE_KEY = "mbip.table-display.v1";
const COLUMN_WIDTHS: readonly TableColumnWidth[] = ["compact", "standard", "wide"];
const ROW_HEIGHTS: readonly TableRowHeight[] = ["compact", "standard", "comfortable"];

export function parseTableDisplayPreferences(value: string | null): TableDisplayPreferences {
  if (!value) return { ...DEFAULT_TABLE_DISPLAY };
  try {
    const parsed = JSON.parse(value) as Partial<TableDisplayPreferences>;
    return {
      columnWidth: COLUMN_WIDTHS.includes(parsed.columnWidth as TableColumnWidth)
        ? parsed.columnWidth as TableColumnWidth
        : DEFAULT_TABLE_DISPLAY.columnWidth,
      rowHeight: ROW_HEIGHTS.includes(parsed.rowHeight as TableRowHeight)
        ? parsed.rowHeight as TableRowHeight
        : DEFAULT_TABLE_DISPLAY.rowHeight,
    };
  } catch {
    return { ...DEFAULT_TABLE_DISPLAY };
  }
}

function initialPreferences() {
  if (typeof window === "undefined") return { ...DEFAULT_TABLE_DISPLAY };
  try {
    return parseTableDisplayPreferences(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return { ...DEFAULT_TABLE_DISPLAY };
  }
}

export function useTableDisplayPreferences() {
  const [preferences, setPreferences] = useState<TableDisplayPreferences>(initialPreferences);
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.tableColumnWidth = preferences.columnWidth;
    root.dataset.tableRowHeight = preferences.rowHeight;
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences)); }
    catch { /* A restricted browser profile may disable local persistence. */ }
  }, [preferences]);
  return [preferences, setPreferences] as const;
}
